import { useEffect, useRef, useState } from 'react'
import { createEmbeddingContext, type EmbeddingEvents, type EventListener } from 'amazon-quicksight-embedding-sdk'
import { useApp, useAuth } from 'hooks'
import { QuickSuiteChatEmbedProps } from './types'

const QuickSuiteChatEmbed = ({ onError, onLoad, agentArn: agentArnOverride }: QuickSuiteChatEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const { repoFactory } = useApp()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const username = user?.username
    if (!username) {
      setError('User not found')
      setIsLoading(false)
      return
    }
    if (!repoFactory) return

    let cancelled = false

    const embedChat = async () => {
      if (!containerRef.current) return

      try {
        const options = agentArnOverride ? { agentArn: agentArnOverride } : undefined
        const { embedUrl, agentArn } = await repoFactory.quickSuiteChatRepo().getQuickSuiteChatUrl(username, options)
        if (cancelled) return

        const embeddingContext = await createEmbeddingContext()
        if (cancelled) return

        const frameOptions = {
          url: embedUrl,
          container: containerRef.current,
          height: '100%',
          width: '100%',
          withIframePlaceholder: true,
          onChange: (changeEvent: { eventName: string }) => {
            if (changeEvent.eventName === 'FRAME_LOADED') {
              setIsLoading(false)
              onLoad?.()
            }
          }
        }

        const onMessage: EventListener = (event: EmbeddingEvents) => {
          if (event.eventName === 'CONTENT_LOADED') {
            setIsLoading(false)
            onLoad?.()
          }
          if (event.eventName === 'ERROR_OCCURRED') {
            const msg = event.message
            const errorCode =
              typeof msg === 'object' && msg !== null && 'errorCode' in msg
                ? String((msg as { errorCode?: string }).errorCode)
                : 'Chat error occurred'
            const err = new Error(errorCode || 'Chat error occurred')
            setError(err.message)
            onError?.(err)
            setIsLoading(false)
          }
        }

        const contentOptions: { onMessage: EventListener; fixedAgentArn?: string } = { onMessage }
        const fixedArn = agentArnOverride ?? agentArn
        if (fixedArn) contentOptions.fixedAgentArn = fixedArn

        await embeddingContext.embedQuickChat(frameOptions, contentOptions)
        if (cancelled) return
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load chat'
          setError(message)
          onError?.(err instanceof Error ? err : new Error(message))
          setIsLoading(false)
        }
      }
    }

    embedChat()
    return () => {
      cancelled = true
    }
  }, [user?.username, repoFactory, agentArnOverride, onError, onLoad])

  if (error) {
    return (
      <div className="p-3 text-danger" role="alert">
        Failed to load chat: {error}
      </div>
    )
  }

  return (
    <div className="position-relative h-100 w-100">
      {isLoading && (
        <div className="position-absolute top-0 start-0 end-0 text-center p-3 text-muted">Loading chat...</div>
      )}
      <div ref={containerRef} style={{ height: '100%', width: '100%', minHeight: '400px' }} />
    </div>
  )
}

export default QuickSuiteChatEmbed
